import React, { useState, useEffect, useCallback } from 'react';
import EnlaceNoDisponible from '../components/EnlaceNoDisponible';

interface Block {
  id: string;
  type: 'text' | 'image' | 'video';
  content: string;
  requires_confirmation: boolean;
}

interface Question {
  id: string;
  type: 'multiple_choice' | 'open_text' | 'scale';
  prompt: string;
  options: string[];
  scale_max: number;
  required: boolean;
}

interface LearningPublic {
  id: string;
  title: string;
  description: string | null;
  status: string;
  expires_at: string | null;
  blocks: Block[];
  questions: Question[];
  sucursal_options: string[];
  departamento_options: string[];
}

const FUNCTION_URL = 'https://uhwuhqhfutvgitgtrxej.supabase.co/functions/v1/learning-public';

export default function LearningPublicPage({ learningId }: { learningId: string }) {
  const [learning, setLearning] = useState<LearningPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<'no_encontrado' | 'cerrado' | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [confirmedBlocks, setConfirmedBlocks] = useState<Record<string, boolean>>({});
  const [nombreApellido, setNombreApellido] = useState('');
  const [email, setEmail] = useState('');
  const [sucursal, setSucursal] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [answers, setAnswers] = useState<Record<string, string | number>>({});

  const fetchLearning = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${FUNCTION_URL}?id=${learningId}`);
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 404) setUnavailable('no_encontrado');
        else if (res.status === 410) setUnavailable('cerrado');
        else setError(data.error || 'No se pudo cargar el formulario');
      } else {
        setLearning(data);
      }
    } catch {
      setError('No se pudo conectar. Intenta de nuevo.');
    }
    setLoading(false);
  }, [learningId]);

  useEffect(() => { fetchLearning(); }, [fetchLearning]);

  function allBlocksConfirmed() {
    if (!learning) return false;
    return learning.blocks.every(b => !b.requires_confirmation || confirmedBlocks[b.id]);
  }

  function allRequiredAnswered() {
    if (!learning) return false;
    return learning.questions.every(q => !q.required || (answers[q.id] !== undefined && answers[q.id] !== ''));
  }

  async function handleSubmit() {
    if (!learning) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learning_id: learning.id,
          email,
          nombre_apellido: nombreApellido,
          sucursal: sucursal || undefined,
          departamento: departamento || undefined,
          answers,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'No se pudo enviar el formulario');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('No se pudo conectar. Intenta de nuevo.');
    }
    setSubmitting(false);
  }

  if (loading) {
    return <div style={wrap}><p style={muted}>Cargando…</p></div>;
  }

  if (unavailable === 'no_encontrado') {
    return <EnlaceNoDisponible message="Este formulario no existe o el enlace está incompleto. Verifica el enlace con quien te lo envió." />;
  }
  if (unavailable === 'cerrado') {
    return <EnlaceNoDisponible title="Formulario cerrado" message="Este formulario ya no está recibiendo respuestas." />;
  }

  if (error && !learning) {
    return <div style={wrap}><div style={card}><p style={{ color: '#ef4444' }}>{error}</p></div></div>;
  }

  if (submitted) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={title}>¡Gracias!</h2>
          <p style={muted}>Tu respuesta fue registrada correctamente.</p>
        </div>
      </div>
    );
  }

  if (!learning) return null;

  const canSubmit = nombreApellido.trim() && email.trim() && allBlocksConfirmed() && allRequiredAnswered() && !submitting;

  return (
    <div style={wrap}>
      <div style={card}>
        <h1 style={title}>{learning.title}</h1>
        {learning.description && <p style={muted}>{learning.description}</p>}

        {learning.blocks.map(block => (
          <div key={block.id} style={blockBox}>
            {block.type === 'text' && <p style={{ color: '#e2e8f0', lineHeight: 1.6 }}>{block.content}</p>}
            {block.type === 'image' && <img src={block.content} alt="" style={{ maxWidth: '100%', borderRadius: 8 }} />}
            {block.type === 'video' && (
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                  src={block.content}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: 8 }}
                  allowFullScreen
                  title="video"
                />
              </div>
            )}
            {block.requires_confirmation && (
              <label style={checkLabel}>
                <input
                  type="checkbox"
                  checked={!!confirmedBlocks[block.id]}
                  onChange={e => setConfirmedBlocks(c => ({ ...c, [block.id]: e.target.checked }))}
                />
                He revisado detenidamente el contenido.
              </label>
            )}
          </div>
        ))}

        <div style={{ borderTop: '1px solid #334155', paddingTop: 16, marginTop: 8 }}>
          <Field label="Nombres y apellidos *">
            <input style={inp} value={nombreApellido} onChange={e => setNombreApellido(e.target.value)} />
          </Field>
          <Field label="Correo institucional *">
            <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </Field>
          {learning.sucursal_options.length > 0 && (
            <Field label="Sucursal">
              <select style={inp} value={sucursal} onChange={e => setSucursal(e.target.value)}>
                <option value="">Selecciona la respuesta</option>
                {learning.sucursal_options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          )}
          {learning.departamento_options.length > 0 && (
            <Field label="Departamento">
              <select style={inp} value={departamento} onChange={e => setDepartamento(e.target.value)}>
                <option value="">Selecciona la respuesta</option>
                {learning.departamento_options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          )}
        </div>

        {learning.questions.map(q => (
          <div key={q.id} style={{ marginTop: 14 }}>
            <Field label={q.prompt + (q.required ? ' *' : '')}>
              {q.type === 'open_text' && (
                <input style={inp} value={(answers[q.id] as string) || ''} onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} />
              )}
              {q.type === 'multiple_choice' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {q.options.map(opt => (
                    <label key={opt} style={checkLabel}>
                      <input type="radio" name={q.id} checked={answers[q.id] === opt} onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))} />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
              {q.type === 'scale' && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Array.from({ length: q.scale_max }, (_, i) => i + 1).map(n => (
                    <label key={n} style={checkLabel}>
                      <input type="radio" name={q.id} checked={answers[q.id] === n} onChange={() => setAnswers(a => ({ ...a, [q.id]: n }))} />
                      {n}
                    </label>
                  ))}
                </div>
              )}
            </Field>
          </div>
        ))}

        {error && <p style={{ color: '#ef4444', marginTop: 12 }}>{error}</p>}

        <p data-testid="learning-nota-un-correo" style={{ ...muted, fontSize: '0.8rem', marginTop: 16, marginBottom: 0 }}>
          Solo se acepta una respuesta por correo en este formulario.
        </p>
        <button onClick={handleSubmit} disabled={!canSubmit} style={{ ...submitBtn, opacity: canSubmit ? 1 : 0.5, marginTop: 10 }}>
          {submitting ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

const wrap: React.CSSProperties = { minHeight: '100vh', background: '#0f172a', display: 'flex', justifyContent: 'center', padding: '32px 16px' };
const card: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, padding: 28, width: '100%', maxWidth: 640 };
const title: React.CSSProperties = { color: '#f1f5f9', fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 };
const muted: React.CSSProperties = { color: '#94a3b8', fontSize: '0.9rem' };
const blockBox: React.CSSProperties = { marginTop: 18, paddingBottom: 14, borderBottom: '1px solid #334155' };
const checkLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, color: '#e2e8f0', fontSize: '0.88rem', marginTop: 10 };
const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const submitBtn: React.CSSProperties = { width: '100%', padding: '12px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 700 };
